import { useState, useMemo } from 'react';
import { Check, MoreHorizontal, X } from 'lucide-react';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  Button, Card, CardBody, Drawer, DrawerContent, DrawerHeader, DrawerTitle,
  DrawerBody, DrawerFooter, IconButton, Input, Skeleton, Textarea,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { EntityLink } from '@/components/entity-link';
import {
  useAffiliatePayouts,
  usePayoutCommissions,
  useUpdatePayout,
  type AffiliatePayout,
  type PayoutCommission,
  type PayoutStatus,
} from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { payoutTone, humanize } from './status-tones';
import { useAffiliatePolicyPermissions } from '../use-affiliate-policy-permissions';
import { affiliateKeys } from '@/api/affiliate';
import { useQueryClient } from '@tanstack/react-query';

const PAGE_SIZE = 20;

interface PayoutsTabProps {
  affiliateId: number;
}

/** Commission line-item columns used inside the expandable sub-row. */
const COMMISSION_COLUMNS: ColumnDef<PayoutCommission, unknown>[] = [
  {
    id: 'invoice_id',
    header: 'Invoice',
    cell: ({ row }) => row.original.invoice_id ? String(row.original.invoice_id) : '—',
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) =>
      row.original.customer_id ? (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.customer_name ?? row.original.customer_email}
        />
      ) : (
        row.original.customer_name || row.original.customer_email || '—'
      ),
  },
  {
    id: 'paid_at',
    header: 'Paid At',
    cell: ({ row }) => formatDate(row.original.effective_payment_at ?? row.original.invoice_payment_at ?? undefined),
  },
  {
    id: 'revenue_amount',
    header: () => <div style={{ textAlign: 'right' }}>Revenue</div>,
    cell: ({ row }) => <div style={{ textAlign: 'right' }}>{formatMoney(row.original.revenue_amount)}</div>,
  },
  {
    id: 'commission_amount',
    header: () => <div style={{ textAlign: 'right' }}>Commission</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
        {formatMoney(row.original.commission_amount)}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.reversed_after_payout_at ? (
        <StatusPill tone="danger">Reversed</StatusPill>
      ) : (
        humanize(row.original.status)
      ),
  },
];

/** Inline sub-row: commission line items for a payout settlement batch. */
function PayoutCommissionsPanel({ payoutId }: { payoutId: number }) {
  const { data: items, isLoading, isError } = usePayoutCommissions(payoutId);

  if (isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} style={{ height: 20 }} />
          ))}
        </CardBody>
      </Card>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 'var(--vn-space-3)' }}>
        <Alert tone="danger" title="Failed to load commission details" />
      </div>
    );
  }

  const commissions: PayoutCommission[] = items ?? [];

  return (
    <Card flat>
      <CardBody>
        <DataTable
          columns={COMMISSION_COLUMNS}
          data={commissions}
          getRowId={(r) => String(r.commission_id)}
          enableSorting={false}
          emptyState={<EmptyState title="No commission line items." />}
        />
      </CardBody>
    </Card>
  );
}

/** Determine which status transitions are allowed for a given payout status. */
function allowedActions(status: PayoutStatus): ('approve' | 'complete' | 'reject')[] {
  switch (status) {
    case 'pending':
      return ['approve', 'reject'];
    case 'approved':
    case 'processing':
      return ['complete', 'reject'];
    default:
      return [];
  }
}

/** Drawer for collecting transaction_ref when completing a payout. */
function CompletePayoutDrawer({
  payout,
  affiliateId,
  onClose,
}: {
  payout: AffiliatePayout | null;
  affiliateId: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [transactionRef, setTransactionRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const update = useUpdatePayout(payout?.payout_id ?? 0);

  const open = !!payout;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTransactionRef('');
      setNotes('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payout) return;
    setError('');
    try {
      await update.mutateAsync({
        status: 'completed',
        transaction_ref: transactionRef.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.payouts(affiliateId) });
      toast.show({ tone: 'success', title: 'Payout marked as completed' });
      handleOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete payout. Please try again.');
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent side="right" size="sm">
        <DrawerHeader>
          <DrawerTitle>Complete Payout #{payout?.payout_id}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <form
            id="complete-payout-form"
            onSubmit={handleSubmit}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Transaction Reference" description="Bank transfer ID, payment gateway reference, etc.">
              {(fp) => (
                <Input
                  {...fp}
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="e.g. TXN-123456"
                />
              )}
            </Field>
            <Field label="Notes">
              {(fp) => (
                <Textarea
                  {...fp}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional completion notes"
                />
              )}
            </Field>
            {error && <Alert tone="danger" title={error} />}
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button form="complete-payout-form" type="submit" loading={update.isPending}>
            Mark as completed
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/** Drawer for collecting rejection_reason when rejecting a payout. */
function RejectPayoutDrawer({
  payout,
  affiliateId,
  onClose,
}: {
  payout: AffiliatePayout | null;
  affiliateId: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const update = useUpdatePayout(payout?.payout_id ?? 0);

  const open = !!payout;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReason('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payout) return;
    setError('');
    try {
      await update.mutateAsync({
        status: 'cancelled',
        rejection_reason: reason.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.payouts(affiliateId) });
      toast.show({ tone: 'success', title: 'Payout rejected' });
      handleOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject payout. Please try again.');
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent side="right" size="sm">
        <DrawerHeader>
          <DrawerTitle>Reject Payout #{payout?.payout_id}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <form
            id="reject-payout-form"
            onSubmit={handleSubmit}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Rejection Reason" description="Explain why this payout is being rejected.">
              {(fp) => (
                <Textarea
                  {...fp}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  placeholder="e.g. Insufficient activity in this period"
                />
              )}
            </Field>
            {error && <Alert tone="danger" title={error} />}
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            form="reject-payout-form"
            type="submit"
            variant="danger"
            loading={update.isPending}
          >
            Reject payout
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/** Shared column definitions for the payouts table — used by the manager tab and the employee self-view. */
export function buildPayoutColumns(): ColumnDef<AffiliatePayout, unknown>[] {
  return [
    {
      id: 'payout_id',
      header: 'Payout ID',
      cell: ({ row }) => row.original.payout_id,
    },
    {
      id: 'amount',
      header: () => <div style={{ textAlign: 'right' }}>Amount</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {formatMoney(row.original.amount)}
        </div>
      ),
    },
    {
      id: 'period',
      header: 'Period',
      cell: ({ row }) => `${row.original.period_start} – ${row.original.period_end}`,
    },
    {
      id: 'payout_method',
      header: 'Method',
      cell: ({ row }) => row.original.payout_method || '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={payoutTone(row.original.status)}>
          {humanize(row.original.status)}
        </StatusPill>
      ),
    },
    {
      id: 'transaction_ref',
      header: 'Transaction Ref',
      cell: ({ row }) => <code>{row.original.transaction_ref || '—'}</code>,
    },
    {
      id: 'requested_at',
      header: 'Requested At',
      cell: ({ row }) => formatDateTime(row.original.requested_at),
    },
  ];
}

/**
 * Payouts tab — paginated payout list with expandable rows showing commission
 * line items and status-gated action menu (approve, complete, reject).
 */
export function PayoutsTab({ affiliateId }: PayoutsTabProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const perms = useAffiliatePolicyPermissions();

  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useAffiliatePayouts(affiliateId, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  /* Approve confirm dialog state */
  const [approveTarget, setApproveTarget] = useState<AffiliatePayout | null>(null);
  const approveMutation = useUpdatePayout(approveTarget?.payout_id ?? 0);

  /* Complete drawer state */
  const [completeTarget, setCompleteTarget] = useState<AffiliatePayout | null>(null);

  /* Reject drawer state */
  const [rejectTarget, setRejectTarget] = useState<AffiliatePayout | null>(null);

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    try {
      await approveMutation.mutateAsync({ status: 'approved' });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.payouts(affiliateId) });
      toast.show({ tone: 'success', title: 'Payout approved' });
      setApproveTarget(null);
    } catch (err) {
      toast.show({
        tone: 'danger',
        title: 'Approve failed',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const columns = useMemo<ColumnDef<AffiliatePayout, unknown>[]>(() => {
    const base = buildPayoutColumns();
    if (!perms.canUpdateAffiliate) return base;

    const actionsCol: ColumnDef<AffiliatePayout, unknown> = {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const payout = row.original;
        const actions = allowedActions(payout.status);
        if (!actions.length) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Payout actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.includes('approve') && (
                <DropdownMenuItem onSelect={() => setApproveTarget(payout)}>
                  <Check size={14} />
                  Approve
                </DropdownMenuItem>
              )}
              {actions.includes('complete') && (
                <DropdownMenuItem onSelect={() => setCompleteTarget(payout)}>
                  <Check size={14} />
                  Mark as completed
                </DropdownMenuItem>
              )}
              {actions.includes('reject') && (
                <DropdownMenuItem onSelect={() => setRejectTarget(payout)} tone="danger">
                  <X size={14} />
                  Reject
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    };

    return [...base, actionsCol];
  }, [perms.canUpdateAffiliate]);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load payouts"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r) => String(r.payout_id)}
        getRowCanExpand={() => true}
        renderSubRow={(row) => <PayoutCommissionsPanel payoutId={row.payout_id} />}
        emptyState={
          <EmptyState
            title="No payouts"
            description="Payout records for this affiliate will appear here."
          />
        }
        footer={
          isLoading ? null : (
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          )
        }
      />

      {/* Approve confirm dialog */}
      <ConfirmDialog
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) setApproveTarget(null); }}
        title="Approve payout"
        description={`Approve payout #${approveTarget?.payout_id} for ${approveTarget ? formatMoney(approveTarget.amount) : ''}?`}
        confirmLabel="Approve"
        loading={approveMutation.isPending}
        onConfirm={handleApproveConfirm}
      />

      {/* Complete drawer */}
      <CompletePayoutDrawer
        payout={completeTarget}
        affiliateId={affiliateId}
        onClose={() => setCompleteTarget(null)}
      />

      {/* Reject drawer */}
      <RejectPayoutDrawer
        payout={rejectTarget}
        affiliateId={affiliateId}
        onClose={() => setRejectTarget(null)}
      />
    </>
  );
}
